import { useCallback } from 'react';

import axios from 'axios';
import { Portal, Project } from 'common/contracts';

import {
  getApiRouteOrMock,
  getCustomHeader,
  EApiRoutes
} from '../../../api/api.utils';
import { useUserDetails } from '../../../api/useUserDetails';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { ProjectCategory } from '../constants';

export function useProjectUpdater() {
  const { userDetails, persistUserDetails } = useUserDetails();

  const updateThumbnailImage = useCallback(
    (itemId: string, category: ProjectCategory, thumbnailImageUrl: string) => {
      if (!userDetails) return;

      const updatedUserDetails = structuredClone(userDetails);

      if (category === ProjectCategory.PROJECT && updatedUserDetails.projects) {
        const projectIndex = updatedUserDetails.projects.findIndex(
          (project: Project) => project.publisherId === itemId
        );
        if (projectIndex !== -1) {
          updatedUserDetails.projects[projectIndex].thumbnailImageUrl =
            thumbnailImageUrl;
        }
      } else if (
        category === ProjectCategory.PORTAL &&
        updatedUserDetails.portals
      ) {
        const portalIndex = updatedUserDetails.portals.findIndex(
          (portal: Portal) => portal.portalId === itemId
        );
        if (portalIndex !== -1) {
          updatedUserDetails.portals[portalIndex].thumbnailImageUrl =
            thumbnailImageUrl;
        }
      }

      persistUserDetails(updatedUserDetails);
    },
    [userDetails, persistUserDetails]
  );

  const updateProjectTags = useCallback(
    async (itemId: string, category: ProjectCategory, tags: string[]) => {
      if (!userDetails) return;

      const updatedUserDetails = structuredClone(userDetails);
      let publisherId: string | undefined;
      let portalId: string | undefined;

      if (category === ProjectCategory.PROJECT && updatedUserDetails.projects) {
        const projectIndex = updatedUserDetails.projects.findIndex(
          (project: Project) => project.publisherId === itemId
        );

        if (projectIndex !== -1) {
          updatedUserDetails.projects[projectIndex].tags = tags;
          publisherId = updatedUserDetails.projects[projectIndex].publisherId;
        }
      } else if (
        category === ProjectCategory.PORTAL &&
        updatedUserDetails.portals
      ) {
        const portalIndex = updatedUserDetails.portals.findIndex(
          (portal: Portal) => portal.portalId === itemId
        );

        if (portalIndex !== -1) {
          updatedUserDetails.portals[portalIndex].tags = tags;
          portalId = updatedUserDetails.portals[portalIndex].portalId;
        }
      }

      persistUserDetails(updatedUserDetails);

      if (publisherId || portalId) {
        try {
          if (publisherId) {
            await axios.put(
              getApiRouteOrMock(EApiRoutes.PUBLISHER),
              {
                publisherId,
                tags
              },
              {
                headers: getCustomHeader(publisherId, undefined)
              }
            );
          } else if (portalId) {
            await axios.put(
              getApiRouteOrMock(EApiRoutes.PORTAL_SETTINGS),
              {
                tags
              },
              {
                headers: { 'x-portal': portalId }
              }
            );
          }

          showToast({
            message: 'Tags updated successfully',
            status: EToasterStatus.SUCCESS
          });
        } catch (error) {
          showToast({
            message: 'Error updating tags',
            status: EToasterStatus.ERROR
          });
        }
      }
    },
    [userDetails, persistUserDetails]
  );

  return {
    updateThumbnailImage,
    updateProjectTags
  };
}
